'use strict';

var StandardTileView = require('components/dashboard/views/standard_tile_view');
var i18n = require('utils/i18n');

var MessageView = StandardTileView.extend({
    template: require('components/dashboard/templates/message.hbs'),
});

var FETCH_STATUS = {
    OK: 0,
    ERROR: 1,
    NO_RESULT: 2,
};

var STATUS = {
    OK: '',
    ERROR: 'error',
};

// Creates a new tile with defaults. For simple tiles, only the template is required. For more complex tiles
// overwrite the methods as needed.
// If getView is provided, no templated is required
function createTile(options, tileDefinition) {
    var defaultTile = {
        previousData: undefined,
        currentData: undefined,
        acknowledged: true,
        lastFetched: undefined,
        lastRequestStatus: undefined,

        getData: function () {
            var def = $.Deferred();

            var self = this;
            if (this.definition.source != '') {
                $.getJSON(this.definition.source, { 'request-time': Date.now() }, function (data) {
                    def.resolve({
                        data: $.extend(data, self.definition.config),
                        fetchedAt: new Date(),
                        status: FETCH_STATUS.OK,
                    });
                }).fail(function () {
                    def.resolve({
                        fetchedAt: new Date(),
                        status: FETCH_STATUS.ERROR,
                    });
                });
                return def;
            }

            // no data to fetch -> no errors possible
            def.resolve({
                data: this.definition.config,
                fetchedAt: new Date(),
                status: FETCH_STATUS.OK,
            });
            return def;
        },

        _needRefresh: function (forceRefresh) {
            // forced refresh or first request
            if (forceRefresh || this.lastRequestStatus === undefined) {
                return true;
            }

            var now = new Date();
            // last request was less than a minute ago? -> leave the backend alone
            if (now - this.lastFetched < 60 * 1000) {
                return false;
            }

            // recently fetched valid data? -> no need to refresh
            if (
                this.lastRequestStatus === FETCH_STATUS.OK &&
                this.lastFetched &&
                now - this.lastFetched < this.definition.refreshRate * 60 * 1000
            ) {
                return false;
            }

            return true;
        },

        // do not override
        update: function (callback, callbackOptions, forceRefresh) {
            if (!this._needRefresh(forceRefresh)) {
                this.currentData = this.currentData || { data: undefined };
                callback(this, this.currentData.data, this.lastRequestStatus, callbackOptions);
                return;
            }

            var self = this;
            $.when(this.getData()).done(function (resp) {
                self.lastFetched = new Date();
                self.lastRequestStatus = resp.status;
                if (resp.status === FETCH_STATUS.ERROR) {
                    resp.data = {
                        title: i18n.TR('Fehler'),
                        text: i18n.TR('Daten konnten nicht geladen werden.'),
                        color_class: 'box-color-error',
                    };
                }

                if (resp.status === FETCH_STATUS.OK) {
                    // if the fetched data is different from the current data, save the current data as old data.
                    if (self.currentData && self.hasChanged(self.currentData.data, resp.data)) {
                        self.previousData = self.currentData;
                        self.acknowledged = false;
                    }
                    self.currentData = resp;
                }

                resp.data = $.extend(resp.data, {
                    header: {
                        title: tileDefinition.title,
                        link: self.getLink(),
                        hasNoFullscreen: tileDefinition.hasNoFullscreen,
                    },
                });
                if (callback) {
                    callback(self, resp.data, resp.status, callbackOptions);
                }
            });
        },

        getTileView: function (data) {
            data.template = options.template;
            return new StandardTileView(data);
        },

        // do not override
        getNoResultView: function (data) {
            data = data || {
                title: i18n.TR('Info'),
                text: i18n.TR('Momentan sind für diese Kachel keine Daten verfügbar.'),
                color_class: 'box-color-info',
            };
            return new MessageView(data);
        },

        _getErrorView: function (data) {
            return new MessageView(
                data || {
                    header: data.header,
                    title: i18n.TR('Fehler'),
                    text: i18n.TR('Daten konnten nicht geladen werden.'),
                    color_class: 'box-color-error',
                }
            );
        },

        // do not override
        getView: function (data, status) {
            switch (status) {
                case FETCH_STATUS.OK:
                    return this.getTileView(data);
                case FETCH_STATUS.NO_RESULT:
                    return this.getNoResultView(data);
                case FETCH_STATUS.ERROR:
                    return this._getErrorView(data);
            }
        },

        hasChanged: function (oldData, newData) {
            return JSON.stringify(oldData) != JSON.stringify(newData);
        },

        getState: function () {
            return STATUS.OK;
        },

        getLink: function () {
            return this.definition.link;
        },

        acknowlege: function () {
            this.acknowledged = true;
        },
    };

    var newTile = $.extend({}, defaultTile, options);
    // custom compare function available? -> override default
    var compareFunction = tileDefinition.compareFunction;
    if (
        compareFunction &&
        window.SDE_plugins &&
        window.SDE_plugins.dashboard &&
        window.SDE_plugins.dashboard.compareFunctions &&
        window.SDE_plugins.dashboard.compareFunctions[tileDefinition.compareFunction]
    ) {
        newTile.hasChanged = window.SDE_plugins.dashboard.compareFunctions[tileDefinition.compareFunction];
    }
    newTile.definition = tileDefinition;
    return newTile;
}

module.exports = {
    FETCH_STATUS: FETCH_STATUS,
    STATUS: STATUS,

    // all available tiles must be specified here
    Library: {
        // 'complex' tiles: a complete tile-object is required which will override some of the default functions
        chart: function (tileConfig) {
            return createTile(require('components/dashboard/tiles/chart_tile'), tileConfig);
        },
        slideshow: function (tileConfig) {
            return createTile(require('components/dashboard/tiles/slideshow_tile'), tileConfig);
        },
        table: function (tileConfig) {
            return createTile(require('components/dashboard/tiles/table_tile'), tileConfig);
        },
        weather: function (tileConfig) {
            return createTile(require('components/dashboard/tiles/weather_tile'), tileConfig);
        },
        changelog: function (tileConfig) {
            return createTile(require('components/dashboard/tiles/changelog_tile'), tileConfig);
        },

        // "simple" tiles: only a template is needed
        video: function (tileConfig) {
            return createTile({ template: require('components/dashboard/templates/video.hbs') }, tileConfig);
        },
    },

    CreateTile: createTile,
};
